import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useApp, LANG_SHORT } from "@/context/AppContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Languages, Moon, Sun, KeyRound, ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck } from "lucide-react";

/**
 * Activate.js — Parcours d'ACTIVATION (première connexion), pensé simple.
 * Étapes : identifiant + code d'invitation → choix du PIN → profil complet → terminé.
 */
export default function Activate() {
  const { activate } = useAuth();
  const { t, cycleLang, lang, theme, toggleTheme } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [profile, setProfile] = useState({ name: "", apartment: "", email: "", phone: "" });
  const [residence] = useState(() => {
    try { return JSON.parse(localStorage.getItem("selectedResidence") || "null"); } catch { return null; }
  });

  // Étape 0 : vérifie identifiant + code AVANT d'aller plus loin, et pré-remplit le profil.
  const checkCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/activate/check", { identifier: identifier.toLowerCase().trim(), code: code.toUpperCase().trim(), residence_id: residence?.id });
      setProfile((p) => ({ ...p, ...data.prefill }));
      setStep(1);
    } catch (err) { toast.error(err?.response?.data?.detail || t("login_error")); }
    finally { setBusy(false); }
  };

  // Étape 1 : valide le PIN et sa confirmation.
  const checkPin = () => {
    if (!/^[0-9]{4,6}$/.test(pin)) { toast.error(t("pin_rule")); return; }
    if (pin !== pin2) { toast.error(t("pin_mismatch")); return; }
    setStep(2);
  };

  // Étape 2 : finalise (profil obligatoire) puis connecte automatiquement.
  const finish = async () => {
    for (const k of ["name", "apartment", "email", "phone"]) {
      if (!profile[k].trim()) { toast.error(t("profile_required")); return; }
    }
    setBusy(true);
    try {
      await activate({ identifier: identifier.toLowerCase().trim(), code: code.toUpperCase().trim(), pin, ...profile, residence_id: residence?.id });
      setStep(3);
    } catch (err) { toast.error(err?.response?.data?.detail || t("login_error")); }
    finally { setBusy(false); }
  };

  const field = "w-full h-14 rounded-2xl bg-white text-foreground px-5 text-base outline-none";

  return (
    <div className="min-h-screen bg-brand text-white relative overflow-hidden flex flex-col" data-testid="activate-page">
      <div className="absolute inset-0 zellige-overlay pointer-events-none" />
      <div className="relative z-10 flex justify-between items-center p-4">
        <button onClick={() => (step > 0 && step < 3 ? setStep(step - 1) : navigate("/login"))}
          data-testid="activate-back" className="h-10 px-3 rounded-full bg-white/15 font-semibold text-sm flex items-center gap-1.5">
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t("back")}
        </button>
        <div className="flex gap-2">
          <button onClick={cycleLang} className="h-10 px-3 rounded-full bg-white/15 font-semibold text-sm flex items-center gap-1.5 [&>*]:pointer-events-none">
            <Languages className="h-4 w-4" />{LANG_SHORT[lang]}
          </button>
          <button onClick={toggleTheme} className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center">
            {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div className="relative z-10 flex-1 flex flex-col justify-center px-6 max-w-md w-full mx-auto pb-10">
        <div className="h-16 w-16 rounded-2xl bg-white/15 flex items-center justify-center mb-6">
          {step === 3 ? <CheckCircle2 className="h-9 w-9" /> : <KeyRound className="h-9 w-9" />}
        </div>

        {/* Indicateur d'étapes */}
        {step < 3 && (
          <div className="flex gap-1.5 mb-5">
            {[0, 1, 2].map((s) => (
              <span key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-white" : "bg-white/25"}`} />
            ))}
          </div>
        )}

        {step === 0 && (
          <>
            <h1 className="text-3xl font-black mb-2">{t("activation_title")}</h1>
            <p className="text-white/70 mb-6">{t("activation_step0_help")}</p>
            <div className="space-y-3">
              <input value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder={t("login_id_ph")}
                autoCapitalize="none" autoCorrect="off" spellCheck="false" data-testid="activate-identifier" className={field} />
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t("invite_code_ph")}
                maxLength={6} data-testid="activate-code" className={`${field} tracking-[0.3em] uppercase`} />
              <button onClick={checkCode} disabled={busy || !identifier || !code} data-testid="activate-check-btn"
                className="w-full h-14 rounded-2xl bg-majorelle text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? t("loading") : t("continue")} <ArrowRight className="h-5 w-5 rtl:rotate-180" />
              </button>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <h1 className="text-3xl font-black mb-2">{t("choose_pin")}</h1>
            <p className="text-white/70 mb-6">{t("pin_rule")}</p>
            <div className="space-y-3">
              <input type="password" inputMode="numeric" maxLength={6} value={pin}
                onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))} placeholder={t("login_pin_ph")}
                data-testid="activate-pin" className={`${field} tracking-[0.4em]`} />
              <input type="password" inputMode="numeric" maxLength={6} value={pin2}
                onChange={(e) => setPin2(e.target.value.replace(/[^0-9]/g, ""))} placeholder={t("confirm_pin")}
                data-testid="activate-pin2" className={`${field} tracking-[0.4em]`} />
              <button onClick={checkPin} disabled={!pin || !pin2} data-testid="activate-pin-btn"
                className="w-full h-14 rounded-2xl bg-majorelle text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60">
                {t("continue")} <ArrowRight className="h-5 w-5 rtl:rotate-180" />
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h1 className="text-3xl font-black mb-2">{t("complete_profile")}</h1>
            <p className="text-white/70 mb-6">{t("profile_help")}</p>
            <div className="space-y-3">
              <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} placeholder={t("full_name")} data-testid="activate-name" className={field} />
              <input value={profile.apartment} onChange={(e) => setProfile({ ...profile, apartment: e.target.value })} placeholder={t("apartment_label")} data-testid="activate-apartment" className={field} />
              <input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} placeholder={t("email")} data-testid="activate-email" className={field} />
              <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder={t("phone_ph")} data-testid="activate-phone" className={field} />
              <button onClick={finish} disabled={busy} data-testid="activate-finish-btn"
                className="w-full h-14 rounded-2xl bg-majorelle text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? t("loading") : t("finish_activation")} <CheckCircle2 className="h-5 w-5" />
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <div className="text-center" data-testid="activate-done">
            <h1 className="text-3xl font-black mb-3">{t("activation_done")}</h1>
            <p className="text-white/80 mb-8">{t("activation_done_help")}</p>
            <div className="rounded-2xl bg-white/10 p-4 mb-6 text-start flex gap-3">
              <ShieldCheck className="h-6 w-6 shrink-0" />
              <p className="text-sm text-white/85">{t("directory_notice")}</p>
            </div>
            <button onClick={() => { localStorage.setItem("pendingHelp", "1"); navigate("/app"); }} data-testid="activate-go-home"
              className="w-full h-14 rounded-2xl bg-majorelle text-white font-bold text-lg flex items-center justify-center gap-2">
              {t("enter_app")} <ArrowRight className="h-5 w-5 rtl:rotate-180" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
